import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { REQUIRED_REFERENCE_COUNT, type ProfileReference } from "@/lib/requiredFields";
import { formatPhoneInput } from "@/lib/phone";

interface Props {
  value: ProfileReference[];
  onChange: (refs: ProfileReference[]) => void;
  /** Per-reference, per-field error messages (parallel to value). */
  errors?: Array<Partial<Record<keyof ProfileReference, string>>>;
}

const EMPTY_REF: ProfileReference = {
  name: "",
  relationship: "",
  phone: "",
  email: "",
};

/**
 * Collects exactly REQUIRED_REFERENCE_COUNT references. Shared by both signup
 * flows (via Step6) and by the backfill fill-in. Each reference = name +
 * relationship + at least one of phone/email.
 */
export function ReferencesInput({ value, onChange, errors }: Props) {
  // Always render REQUIRED_REFERENCE_COUNT slots.
  const refs: ProfileReference[] = Array.from(
    { length: REQUIRED_REFERENCE_COUNT },
    (_, i) => value[i] ?? { ...EMPTY_REF },
  );

  const update = (i: number, patch: Partial<ProfileReference>) => {
    const next = refs.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
    onChange(next);
  };

  return (
    <div className="space-y-6">
      {refs.map((ref, i) => {
        const e = errors?.[i] ?? {};
        return (
          <div
            key={i}
            className="space-y-3 rounded-md border border-border bg-muted/20 p-4"
          >
            <p className="text-sm font-medium text-foreground">
              Reference {i + 1}
            </p>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor={`ref-${i}-name`} className="text-sm">
                  Name *
                </Label>
                <Input
                  id={`ref-${i}-name`}
                  value={ref.name}
                  onChange={(ev) => update(i, { name: ev.target.value })}
                  placeholder="Full name"
                />
                {e.name && (
                  <p className="text-sm text-destructive">{e.name}</p>
                )}
              </div>

              <div className="space-y-1">
                <Label htmlFor={`ref-${i}-relationship`} className="text-sm">
                  How they know you *
                </Label>
                <Input
                  id={`ref-${i}-relationship`}
                  value={ref.relationship}
                  onChange={(ev) =>
                    update(i, { relationship: ev.target.value })
                  }
                  placeholder="e.g. Imam, family friend, mentor"
                />
                {e.relationship && (
                  <p className="text-sm text-destructive">{e.relationship}</p>
                )}
              </div>

              <div className="space-y-1">
                <Label htmlFor={`ref-${i}-phone`} className="text-sm">
                  Phone
                </Label>
                <Input
                  id={`ref-${i}-phone`}
                  type="tel"
                  value={ref.phone ?? ""}
                  onChange={(ev) =>
                    update(i, { phone: formatPhoneInput(ev.target.value) })
                  }
                  placeholder="(555) 123-4567"
                />
                {e.phone && (
                  <p className="text-sm text-destructive">{e.phone}</p>
                )}
              </div>

              <div className="space-y-1">
                <Label htmlFor={`ref-${i}-email`} className="text-sm">
                  Email
                </Label>
                <Input
                  id={`ref-${i}-email`}
                  type="email"
                  value={ref.email ?? ""}
                  onChange={(ev) => update(i, { email: ev.target.value })}
                  placeholder="name@example.com"
                />
                {e.email && (
                  <p className="text-sm text-destructive">{e.email}</p>
                )}
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Provide at least a phone or an email so we can reach them.
            </p>
          </div>
        );
      })}
    </div>
  );
}
